import {createSlice, PayloadAction} from '@reduxjs/toolkit'
import {RootState} from '../store/Store'
import { IFilterDto, TSort } from '../../common/model/Core.model'
import { userApi } from './User.api'
import { IUserDto } from './User.model'
import { signOut } from '../auth/Auth.slice'

type InitialState = {
  filtersSelection: IFilterDto[]
  users: {
    error?: string
    loading: boolean
    data: IUserDto[]
    pagination?: {
      limit?: number
      sort?: TSort
      page?: string | undefined
    }
    filters?: IFilterDto[]
  }
  user: {
    error?: string
    loading: boolean
    data?: IUserDto
  }
}

const initialState = {
  filtersSelection: [],
  users: {
    error: undefined,
    loading: false,
    data: [],
    pagination: {
      limit: 25,
      sort: 'ASC',
      page: undefined,
    },
    filters: undefined,
  },
  user: {
    error: undefined,
    loading: false,
    data: undefined,
  },
} as InitialState

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setUserFiltersSelection: (state, action: PayloadAction<IFilterDto[]>) => {
      state.filtersSelection = action.payload
    },
  },
  extraReducers(builder) {
      builder
        .addCase(signOut, () => initialState)
        //Search
        .addMatcher(userApi.endpoints.userSearch.matchPending, (state) => {
          return {
            ...state,
            users: {
              ...initialState.users,
              error: undefined,
              loading: true,
            },
          }
        })
        .addMatcher(userApi.endpoints.userSearch.matchFulfilled, (state, action) => {
          return {
            ...state,
            users: {
              ...state.users,
              error: undefined,
              loading: false,
              data: action.payload.data,
              pagination: action.payload.pagination,
              filters: action.payload.filters,
            },
          }
        })
        .addMatcher(userApi.endpoints.userSearch.matchRejected, (state, action) => {
          return {
            ...state,
            users: {
              ...state.users,
              error: action.error.message,
              loading: false,
            },
          }
        })
        //LoadMore
        .addMatcher(userApi.endpoints.userLoadMore.matchPending, (state) => {
          return {
            ...state,
            users: {
              ...state.users,
              error: undefined,
              loading: true,
            },
          }
        })
        .addMatcher(userApi.endpoints.userLoadMore.matchFulfilled, (state, action) => {
          return {
            ...state,
            users: {
              ...state.users,
              error: undefined,
              loading: false,
              data: state.users.data.concat(action.payload.data),
              pagination: action.payload.pagination,
              filters: action.payload.filters,
            },
          }
        })
        .addMatcher(userApi.endpoints.userLoadMore.matchRejected, (state, action) => {
          return {
            ...state,
            users: {
              ...state.users,
              error: action.error.message,
              loading: false,
            },
          }
        })
        //GetById
        .addMatcher(userApi.endpoints.userGetById.matchPending, (state) => {
          return {
            ...state,
            user: {
              ...initialState.user,
              error: undefined,
              loading: true,
            },
          }
        })
        .addMatcher(userApi.endpoints.userGetById.matchFulfilled, (state, action) => {
          return {
            ...state,
            user: {
              ...state.user,
              error: undefined,
              loading: false,
              data: action.payload,
            },
          }
        })
        .addMatcher(userApi.endpoints.userGetById.matchRejected, (state, action) => {
          return {
            ...state,
            user: {
              ...state.user,
              error: action.error.message,
              loading: false,
            },
          }
        })
        //Create
        .addMatcher(userApi.endpoints.userCreate.matchPending, (state) => {
          state.user = {
            ...initialState.user,
            error: undefined,
            loading: true,
          }
        })
        .addMatcher(userApi.endpoints.userCreate.matchFulfilled, (state, action) => {
          state.user = {
            error: undefined,
            loading: false,
            data: action.payload,
          }
        })
        .addMatcher(userApi.endpoints.userCreate.matchRejected, (state, action) => {
          state.user = {
            ...initialState.user,
            error: action.error.message,
            loading: false,
          }
        })
        //DeleteById
        .addMatcher(userApi.endpoints.userDeleteById.matchPending, (state) => {
          return {
            ...state,
            user: {
              ...initialState.user,
              error: undefined,
              loading: true,
            },
          }
        })
        .addMatcher(userApi.endpoints.userDeleteById.matchFulfilled, (state, action) => {
          return {
            ...state,
            user: {
              ...state.user,
              error: undefined,
              loading: false,
            },
          }
        })
        .addMatcher(userApi.endpoints.userDeleteById.matchRejected, (state, action) => {
          return {
            ...state,
            user: {
              ...state.user,
              error: action.error.message,
              loading: false,
            },
          }
        })
        //UpdateById
        .addMatcher(userApi.endpoints.userUpdateById.matchPending, (state) => {
          return {
            ...state,
            user: {
              ...state.user,
              error: undefined,
              loading: true
            },
          }
        })
        .addMatcher(userApi.endpoints.userUpdateById.matchFulfilled, (state, action) => {
          return {
            ...state,
            user: {
              ...state.user,
              error: undefined,
              loading: false,
              data: action.payload,
            },
          }
        })
        .addMatcher(userApi.endpoints.userUpdateById.matchRejected, (state, action) => {
          return {
            ...state,
            user: {
              ...state.user,
              error: action.error.message,
              loading: false,
            },
          }
        })
  },
})

export const {setUserFiltersSelection} = userSlice.actions
export default userSlice.reducer

export const selectUsers = (state: RootState) => state.userReducer.users.data
export const selectUsersLoading = (state: RootState) => state.userReducer.users.loading
export const selectUsersPagination = (state: RootState) => state.userReducer.users.pagination
export const selectUsersFilters = (state: RootState) => state.userReducer.users.filters
export const selectUserFiltersSelection = (state: RootState) => state.userReducer.filtersSelection

export const selectUser = (state: RootState) => state.userReducer.user.data
export const selectUserLoading = (state: RootState) => state.userReducer.user.loading
